/**
 * VoterVerify i18n Configuration
 *
 * Consolidated internationalization setup for English/Tamil bilingual support.
 * Uses i18next with react-i18next for React components.
 *
 * Features:
 * - English (en) and Tamil (ta) language support
 * - LocalStorage persistence for language preference
 * - Tamil font configuration with proper typography
 * - Meta tag updates for SEO
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import enTranslations from './locales/en.json';
import taTranslations from './locales/ta.json';

/**
 * Tamil font configuration
 * Injects optimized Tamil fonts and typography styles
 */
const configureTamilFonts = (): void => {
  // Check if already configured
  if (document.getElementById('tamil-font-styles')) {
    return;
  }

  const style = document.createElement('style');
  style.id = 'tamil-font-styles';
  style.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=Noto+Sans+Tamil:wght@400;500;600;700&display=swap');

    /* Tamil text with proper line-height and letter-spacing */
    .tamil-text {
      font-family: 'Noto Sans Tamil', 'Lohit Tamil', 'Tamil MN', system-ui, sans-serif;
      line-height: 1.7;
      letter-spacing: 0.02em;
      word-spacing: 0.05em;
    }

    /* Tamil headings with adjusted line-height */
    .tamil-heading {
      font-family: 'Noto Sans Tamil', 'Lohit Tamil', 'Tamil MN', system-ui, sans-serif;
      font-weight: 600;
      line-height: 1.4;
      letter-spacing: 0.01em;
    }

    /* Tamil UI elements with compact styling */
    .tamil-ui {
      font-family: 'Noto Sans Tamil', 'Lohit Tamil', 'Tamil MN', system-ui, sans-serif;
      font-size: 0.95em;
      line-height: 1.5;
      letter-spacing: 0.01em;
    }

    /* Mobile optimization for Tamil text */
    @media (max-width: 768px) {
      .tamil-text {
        font-size: 0.9em;
        line-height: 1.8;
      }

      .tamil-heading {
        font-size: 0.95em;
        line-height: 1.5;
      }

      .tamil-ui {
        font-size: 0.85em;
        line-height: 1.6;
      }
    }

    /* Tamil selection styling */
    .tamil-text::selection,
    .tamil-heading::selection,
    .tamil-ui::selection {
      background: #3b82f6;
      color: #ffffff;
    }

    /* Focus styles for Tamil inputs */
    .tamil-ui:focus,
    .tamil-ui:focus-visible {
      outline: 2px solid #2563eb;
      outline-offset: 2px;
    }
  `;
  document.head.appendChild(style);
};

/**
 * Update meta tags for the current language
 * Helps with SEO and social media sharing
 */
const updateMetaTags = (language: string): void => {
  const titles: Record<string, string> = {
    ta: 'தமிழ்நாடு வாக்காளர் சரிபார்ப்பு அமைப்பு',
    en: 'Tamil Nadu Voter Verification System'
  };

  const descriptions: Record<string, string> = {
    ta: 'தமிழ்நாடு வாக்காளர் சரிபார்ப்பு அமைப்பு - டிஜிட்டல் தமிழ் ஆதரவுடன்',
    en: 'Tamil Nadu Voter Verification System - Digital voter management platform with bilingual support'
  };

  const locales: Record<string, string> = {
    ta: 'ta_IN',
    en: 'en_US'
  };

  // Update document title
  document.title = titles[language] || titles.en;

  // Update meta description
  let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement;
  if (!metaDesc) {
    metaDesc = document.createElement('meta');
    metaDesc.name = 'description';
    document.head.appendChild(metaDesc);
  }
  metaDesc.content = descriptions[language] || descriptions.en;

  // Update og:title
  let ogTitle = document.querySelector('meta[property="og:title"]') as HTMLElement;
  if (!ogTitle) {
    ogTitle = document.createElement('meta');
    (ogTitle as HTMLMetaElement).setAttribute('property', 'og:title');
    document.head.appendChild(ogTitle);
  }
  (ogTitle as HTMLMetaElement).content = titles[language] || titles.en;

  // Update og:description
  let ogDesc = document.querySelector('meta[property="og:description"]') as HTMLElement;
  if (!ogDesc) {
    ogDesc = document.createElement('meta');
    (ogDesc as HTMLMetaElement).setAttribute('property', 'og:description');
    document.head.appendChild(ogDesc);
  }
  (ogDesc as HTMLMetaElement).content = descriptions[language] || descriptions.en;

  // Update og:locale
  let ogLocale = document.querySelector('meta[property="og:locale"]') as HTMLElement;
  if (!ogLocale) {
    ogLocale = document.createElement('meta');
    (ogLocale as HTMLMetaElement).setAttribute('property', 'og:locale');
    document.head.appendChild(ogLocale);
  }
  (ogLocale as HTMLMetaElement).content = locales[language] || locales.en;
};

/**
 * Apply language-specific styles to document
 */
const applyLanguageStyles = (language: string): void => {
  const root = document.documentElement;

  // Update language and direction
  root.lang = language;
  root.dir = 'ltr'; // Both English and Tamil are LTR

  // Apply font family
  if (language === 'ta') {
    root.style.fontFamily = "'Noto Sans Tamil', 'Lohit Tamil', 'Tamil MN', system-ui, sans-serif";
    document.body.classList.add('tamil-text');
  } else {
    root.style.fontFamily = "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";
    document.body.classList.remove('tamil-text');
  }

  // Update meta tags
  updateMetaTags(language);
};

/**
 * Initialize i18next with configuration
 */
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    // Fallback language
    fallbackLng: 'en',

    // Available languages
    supportedLngs: ['en', 'ta'],

    // Debug in development
    debug: process.env.NODE_ENV === 'development',

    // Interpolation configuration
    interpolation: {
      escapeValue: false, // React already escapes values
    },

    // Language detection configuration
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'voterVerify_language',
    },

    // Translation resources
    resources: {
      en: {
        translation: enTranslations
      },
      ta: {
        translation: taTranslations
      }
    },

    // React configuration
    react: {
      useSuspense: false,
    }
  });

/**
 * Configure Tamil fonts when i18n is initialized
 */
i18n.on('initialized', () => {
  configureTamilFonts();

  // Apply initial language styles
  applyLanguageStyles(i18n.language);
});

/**
 * Handle language changes
 */
i18n.on('languageChanged', (lng) => {
  applyLanguageStyles(lng);

  // Dispatch custom event for components to listen to
  window.dispatchEvent(new CustomEvent('languageChanged', {
    detail: { language: lng }
  }));
});

/**
 * Tamil utility functions for use in components
 */
export const tamilUtils = {
  /**
   * Check if current language is Tamil
   */
  isTamilLanguage: (): boolean => i18n.language === 'ta',

  /**
   * Get the current language
   */
  getCurrentLanguage: (): string => i18n.language,

  /**
   * Format text with appropriate Tamil class
   */
  getTextClass: (type: 'text' | 'heading' | 'ui' = 'text'): string => {
    if (i18n.language !== 'ta') return '';
    return type === 'text' ? 'tamil-text' :
           type === 'heading' ? 'tamil-heading' :
           'tamil-ui';
  },

  /**
   * Format numbers for Tamil locale
   */
  formatNumber: (num: number): string => {
    if (i18n.language === 'ta') {
      return new Intl.NumberFormat('ta-IN').format(num);
    }
    return new Intl.NumberFormat('en-IN').format(num);
  },

  /**
   * Format dates for Tamil locale
   */
  formatDate: (date: Date): string => {
    if (i18n.language === 'ta') {
      return date.toLocaleDateString('ta-IN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  },

  /**
   * Format currency for Tamil locale
   */
  formatCurrency: (amount: number): string => {
    if (i18n.language === 'ta') {
      return new Intl.NumberFormat('ta-IN', {
        style: 'currency',
        currency: 'INR'
      }).format(amount);
    }
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  },

  /**
   * Check if text contains Tamil characters
   */
  isTamilText: (text: string): boolean => {
    return /[\u0B80-\u0BFF]/.test(text);
  }
};

export default i18n;
